import DateInput from "@/src/components/ui-kit/date-input";
import ScInput from "@/src/components/ui-kit/input";
import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { EditProfileFormValues } from "@/src/models/user.model";
import { useTheme } from "@/src/styles/theme-provider";
import { Edit } from "iconsax-react-nativejs";
import { useEffect, useRef } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import CountryPicker from "./country-picker";
import GenderPicker from "./gender-picker";

type BackgroundImageProps = Readonly<{
  backgroundImageUrl: string;
  canEdit: boolean;
  isUploading: boolean;
  onPress: () => void;
}>;

const EditProfileBackgroundImage = ({
  backgroundImageUrl,
  canEdit,
  isUploading,
  onPress,
}: BackgroundImageProps) => {
  const { themeVars } = useTheme();

  return (
  <View className="relative rounded-xl overflow-hidden">
    {backgroundImageUrl ? (
      <Image
        source={{ uri: backgroundImageUrl }}
        style={{ width: "100%", height: 300 }}
        resizeMode="cover"
      />
    ) : (
      <View className="w-full h-[200px] bg-ash" />
    )}
    {canEdit && (
      <Pressable
        onPress={onPress}
        className="absolute bottom-2 right-2 bg-chalk px-2 py-2 rounded-full flex-row items-center gap-1 hover:bg-ash-3"
      >
        {isUploading ? (
          <ActivityIndicator size="small" />
        ) : (
          <Edit size={20} color={themeVars["--color-ash-2"] as string} />
        )}
      </Pressable>
    )}
  </View>
  );
};

type AvatarProps = Readonly<{
  profileImageUrl: string | null;
  firstName?: string;
  lastName?: string;
  canEdit: boolean;
  isUploading: boolean;
  onPress: () => void;
}>;

const EditProfileAvatar = ({
  profileImageUrl,
  firstName,
  lastName,
  canEdit,
  isUploading,
  onPress,
}: AvatarProps) => {
  const { themeVars } = useTheme();

  return (
  <View className="items-center gap-2">
    <View className="relative">
      <Pressable onPress={canEdit ? onPress : undefined}>
        {profileImageUrl ? (
          <Image
            source={{ uri: profileImageUrl }}
            resizeMode="cover"
            style={{ width: 100, height: 100, borderRadius: 50 }}
          />
        ) : (
          <UserAvatar firstName={firstName} lastName={lastName} size={100} />
        )}
      </Pressable>
      {canEdit && (
        <Pressable
          onPress={onPress}
          hitSlop={8}
          className="absolute bottom-1 right-1 bg-chalk p-1 rounded-full hover:bg-ash-3"
        >
          {isUploading ? (
            <ActivityIndicator size="small" />
          ) : (
            <Edit size={20} color={themeVars["--color-ash-2"] as string} />
          )}
        </Pressable>
      )}
    </View>
  </View>
  );
};

type DobPickerProps = Readonly<{
  value: Date | null;
  canEdit: boolean;
  onChange: (date: Date | null) => void;
}>;

const DobPicker = ({ value, canEdit, onChange }: DobPickerProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
  }, []);

  const initialDay = value ? String(value.getDate()).padStart(2, "0") : "";
  const initialMonth = value
    ? String(value.getMonth() + 1).padStart(2, "0")
    : "";
  const initialYear = value ? String(value.getFullYear()) : "";

  const handleChange = (d: string, m: string, y: string) => {
    if (!mounted.current) return;
    const day = parseInt(d, 10);
    const month = parseInt(m, 10);
    const year = parseInt(y, 10);
    // Accept single-digit day/month (the input auto-advances on e.g. "6"); only
    // the 4-digit year is required. Build the date numerically so non-padded
    // values like "1990-6-5" don't produce an Invalid Date.
    if (
      y.length === 4 &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12 &&
      !Number.isNaN(year)
    ) {
      const parsed = new Date(year, month - 1, day);
      if (!Number.isNaN(parsed.getTime())) {
        onChange(parsed);
        return;
      }
    }
    onChange(null);
  };

  const displayValue = value
    ? `${String(value.getDate()).padStart(2, "0")} / ${String(value.getMonth() + 1).padStart(2, "0")} / ${value.getFullYear()}`
    : null;

  const borderColor = themeVars["--color-ash-3"];
  const backgroundColor = themeVars["--color-ash-default"];

  return (
    <View className="flex-1 gap-1">
      <Text className="text-base text-onyx">{t("LABEL_DATE_OF_BIRTH")}</Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderWidth: isMobile ? 0 : 1,
          borderRadius: 10,
          paddingHorizontal: 12,
          height: 50,
          borderColor,
          backgroundColor,
        }}
      >
        {canEdit ? (
          <DateInput
            initialDay={initialDay}
            initialMonth={initialMonth}
            initialYear={initialYear}
            onChange={handleChange}
            className="flex-1"
          />
        ) : (
          <Text
            style={{
              color: themeVars["--color-slate-default"],
              fontSize: 16,
              flex: 1,
            }}
          >
            {displayValue ?? t("LABEL_ENTER_DATE_OF_BIRTH")}
          </Text>
        )}
      </View>
    </View>
  );
};

type PersonalDetailsSectionProps = Readonly<{
  control: Control<EditProfileFormValues>;
  canEditFields: boolean;
  backgroundImageUrl: string;
  profileImageUrl: string | null;
  firstName?: string;
  lastName?: string;
  isUploadingProfile: boolean;
  isUploadingBackground: boolean;
  onPickBackgroundImage: () => void;
  onPickProfileImage: () => void;
  genderAndDobInRow?: boolean;
}>;

const PersonalDetailsSection = ({
  control,
  canEditFields,
  backgroundImageUrl,
  profileImageUrl,
  firstName,
  lastName,
  isUploadingProfile,
  isUploadingBackground,
  onPickBackgroundImage,
  onPickProfileImage,
  genderAndDobInRow = false,
}: PersonalDetailsSectionProps) => {
  // initially-empty inputs reject typed input.
  "use no memo";
  const { t } = useTranslation();

  const genderField = (
    <Controller
      control={control}
      name="gender"
      render={({ field: { value, onChange } }) => (
        <GenderPicker
          value={value}
          canEdit={canEditFields}
          onChange={onChange}
        />
      )}
    />
  );
  const dobField = (
    <Controller
      control={control}
      name="dob"
      render={({ field: { value, onChange } }) => {
        return (
          <DobPicker
            value={value}
            canEdit={canEditFields}
            onChange={onChange}
          />
        );
      }}
    />
  );

  return (
    <View className="gap-4">
      <Text className="font-semibold text-base text-onyx">
        {t("LABEL_PERSONAL_DETAILS")}
      </Text>

      <EditProfileBackgroundImage
        backgroundImageUrl={backgroundImageUrl}
        canEdit={canEditFields}
        isUploading={isUploadingBackground}
        onPress={onPickBackgroundImage}
      />

      <EditProfileAvatar
        profileImageUrl={profileImageUrl}
        firstName={firstName}
        lastName={lastName}
        canEdit={canEditFields}
        isUploading={isUploadingProfile}
        onPress={onPickProfileImage}
      />

      <View className="flex-col md:flex-row gap-4">
        <View className="flex-1">
          <Controller
            control={control}
            name="firstName"
            rules={{
              validate: (value) =>
                (value ?? "").trim().length > 0 || "ERROR_FIRST_NAME_REQUIRED",
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ScInput
                label="LABEL_FIRST_NAME"
                placeholder="LABEL_ENTER_FIRST_NAME"
                value={value}
                onChangeText={onChange}
                editable={canEditFields}
                error={error?.message}
              />
            )}
          />
        </View>
        <View className="flex-1">
          <Controller
            control={control}
            name="lastName"
            render={({ field: { value, onChange } }) => (
              <ScInput
                label="LABEL_LAST_NAME"
                placeholder="LABEL_ENTER_LAST_NAME"
                value={value}
                onChangeText={onChange}
                editable={canEditFields}
              />
            )}
          />
        </View>
      </View>

      {genderAndDobInRow ? (
        <View className="flex-col md:flex-row gap-4">
          <View className="flex-1">{genderField}</View>
          <View className="flex-1">{dobField}</View>
        </View>
      ) : (
        <>
          {genderField}
          {dobField}
        </>
      )}

      <Controller
        control={control}
        name="description"
        render={({ field: { value, onChange } }) => (
          <ScInput
            label="LABEL_DESCRIPTION"
            placeholder="LABEL_DESCRIBE_YOURSELF"
            value={value}
            onChangeText={onChange}
            editable={canEditFields}
          />
        )}
      />

      <View className="flex-col md:flex-row gap-4">
        <View className="flex-1">
          <Controller
            control={control}
            name="address"
            render={({ field: { value, onChange } }) => (
              <ScInput
                label="LABEL_ADDRESS"
                placeholder="LABEL_ENTER_ADDRESS"
                value={value}
                onChangeText={onChange}
                editable={canEditFields}
              />
            )}
          />
        </View>
        <View className="flex-1">
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { value, onChange } }) => (
              <ScInput
                label="LABEL_PHONE"
                placeholder="LABEL_ENTER_PHONE"
                value={value}
                onChangeText={onChange}
                editable={canEditFields}
              />
            )}
          />
        </View>
      </View>

      <Controller
        control={control}
        name="country"
        render={({ field: { value, onChange } }) => (
          <CountryPicker
            value={value}
            canEdit={canEditFields}
            onChange={onChange}
          />
        )}
      />
    </View>
  );
};

export default PersonalDetailsSection;
